import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface LeaveRequest {
  id: string;
  staffId: string;
  staffName: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: string;
  daysRequested: number;
  requestedAt: string;
  approvedAt?: string;
  approvedBy?: string;
}

interface LeaveBalance {
  staffName: string;
  leaveType: string;
  entitlement: number;
  used: number;
  available: number;
  pending: number;
}

export default function LeaveRequestsManager() {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [leaveType, setLeaveType] = useState('annual');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await apiClient.getLeaveRequests('', filterStatus !== 'all' ? filterStatus : undefined);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalances = async (id: string) => {
    try {
      const response = await apiClient.getLeaveBalance(id);
      setBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balance:', error);
    }
  };

  const handleRequestLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.requestLeave(staffId, leaveType, startDate, endDate, reason);
      setShowRequestForm(false);
      setStartDate('');
      setEndDate('');
      setReason('');
      fetchRequests();
      fetchBalances(staffId);
    } catch (error) {
      console.error('Failed to request leave:', error);
    }
  };

  const handleApprove = async (leaveId: string) => {
    try {
      await apiClient.approveLeave(leaveId, 'Manager');
      fetchRequests();
    } catch (error) {
      console.error('Failed to approve leave:', error);
    }
  };

  const handleReject = async (leaveId: string) => {
    if (!rejectionReason) {
      alert('Please provide a rejection reason');
      return;
    }
    try {
      await apiClient.rejectLeave(leaveId, 'Manager', rejectionReason);
      setSelectedLeaveId(null);
      setRejectionReason('');
      fetchRequests();
    } catch (error) {
      console.error('Failed to reject leave:', error);
    }
  };

  const handleCancel = async (leaveId: string) => {
    try {
      await apiClient.cancelLeave(leaveId);
      fetchRequests();
    } catch (error) {
      console.error('Failed to cancel leave:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: '#FFA500',
      approved: '#28a745',
      rejected: '#dc3545',
      cancelled: '#6c757d',
    };
    return colors[status] || '#000';
  };

  const getLeaveTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      annual: '🏖️ Annual Leave',
      sick: '🤒 Sick Leave',
      compassionate: '🕯️ Compassionate Leave',
      unpaid: '❌ Unpaid Leave',
    };
    return labels[type] || type;
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Leave Request Management</h2>

      {showRequestForm && (
        <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ccc', borderRadius: '8px' }}>
          <h3>Request Leave</h3>
          <form onSubmit={handleRequestLeave}>
            <div style={{ marginBottom: '10px' }}>
              <label>Staff ID:</label>
              <input
                type="text"
                value={staffId}
                onChange={(e) => {
                  setStaffId(e.target.value);
                  if (e.target.value) fetchBalances(e.target.value);
                }}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label>Leave Type:</label>
              <select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              >
                <option value="annual">Annual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="compassionate">Compassionate Leave</option>
                <option value="unpaid">Unpaid Leave</option>
              </select>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label>Reason:</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px', minHeight: '80px' }}
              />
            </div>

            {balances.length > 0 && (
              <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <strong>Leave Balance:</strong>
                {balances.map((b) => (
                  <div key={b.leaveType} style={{ marginTop: '5px', fontSize: '14px' }}>
                    {getLeaveTypeLabel(b.leaveType)}: {b.available} available ({b.used} used, {b.pending} pending)
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Request Leave
              </button>
              <button
                type="button"
                onClick={() => setShowRequestForm(false)}
                style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowRequestForm(!showRequestForm)}
          style={{ padding: '10px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}
        >
          {showRequestForm ? 'Hide Form' : 'New Leave Request'}
        </button>

        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={{
                padding: '8px 16px',
                backgroundColor: filterStatus === status ? '#007bff' : '#f0f0f0',
                color: filterStatus === status ? '#fff' : '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Staff</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Leave Type</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Dates</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Days</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '10px' }}>{req.staffName}</td>
                  <td style={{ padding: '10px' }}>{getLeaveTypeLabel(req.leaveType)}</td>
                  <td style={{ padding: '10px' }}>
                    {new Date(req.startDate).toLocaleDateString()} - {new Date(req.endDate).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{req.daysRequested}</td>
                  <td style={{ padding: '10px' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        backgroundColor: getStatusColor(req.status),
                        color: '#fff',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textTransform: 'capitalize',
                      }}
                    >
                      {req.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>
                    {req.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          onClick={() => handleApprove(req.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => setSelectedLeaveId(req.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          ✗ Reject
                        </button>
                      </div>
                    )}
                    {req.status === 'approved' && (
                      <button
                        onClick={() => handleCancel(req.id)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedLeaveId && (
        <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #dc3545', borderRadius: '8px' }}>
          <h3>Reject Leave Request</h3>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
            style={{ width: '100%', padding: '10px', marginBottom: '10px', minHeight: '80px' }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => handleReject(selectedLeaveId)}
              style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Confirm Rejection
            </button>
            <button
              onClick={() => {
                setSelectedLeaveId(null);
                setRejectionReason('');
              }}
              style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
